/**
 * Target of an SBOM upload onto one product (DEV-107): either a NEW official
 * version or an UPDATE of an existing official one. Custom versions are not
 * offered — they are updated through the upload on their product instance.
 *
 * The panel owns loading the product and its versions; the page only receives
 * the resulting request (or null while the choice is incomplete).
 */

import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';

import { Banner } from '../../components/Banner';
import Spinner from '../../components/Spinner';
import { errorMessage, fetchCmdbCard, fetchCmdbCards } from '../../lib/api';
import { intlLocale } from '../../lib/locale';
import type { CmdbCardRow, ImportTargetRequest } from '../../lib/types';

export interface ProductTargetState {
  productName: string | null;
  request: ImportTargetRequest | null;
}

interface ImportTargetPanelProps {
  productId: number;
  /** Opened from a version row: update that official version. */
  initialVersionId?: number | null;
  disabled: boolean;
  onChange: (state: ProductTargetState) => void;
}

function versionLabel(row: CmdbCardRow): string {
  const nr = row.values.VersionNr;
  return typeof nr === 'string' && nr !== '' ? nr : row.label;
}

export function ImportTargetPanel({
  productId, initialVersionId = null, disabled, onChange,
}: ImportTargetPanelProps): ReactNode {
  const { t } = useLingui();
  const idPrefix = useId();
  const [product, setProduct] = useState<CmdbCardRow | null>(null);
  const [versions, setVersions] = useState<readonly CmdbCardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mode, setMode] = useState<'new' | 'update'>(initialVersionId !== null ? 'update' : 'new');
  const [versionNr, setVersionNr] = useState('');
  const [predecessorId, setPredecessorId] = useState<number | null>(null);
  const [versionId, setVersionId] = useState<number | null>(initialVersionId);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    Promise.all([fetchCmdbCard('ProduktType', productId), fetchCmdbCards('ProduktVersion')])
      .then(([productRow, versionList]) => {
        if (cancelled) return;
        setProduct(productRow);
        setVersions(
          versionList.rows
            .filter((row) => row.values.ProduktTypeID === productId)
            .filter((row) => row.values.VersionType !== 'CUSTOM')
            .sort((a, b) => versionLabel(a).localeCompare(versionLabel(b), intlLocale(), { numeric: true })),
        );
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(errorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productId]);

  const taken = useMemo(
    () => new Set(versions.map((row) => versionLabel(row).trim().toLowerCase())),
    [versions],
  );
  const trimmedNr = versionNr.trim();
  const duplicate = mode === 'new' && trimmedNr !== '' && taken.has(trimmedNr.toLowerCase());

  const request = useMemo<ImportTargetRequest | null>(() => {
    if (product === null) return null;
    if (mode === 'new') {
      if (trimmedNr === '' || duplicate) return null;
      return {
        target: 'product', product_type_id: productId, mode,
        version_nr: trimmedNr, predecessor_id: predecessorId ?? undefined,
      };
    }
    if (versionId === null) return null;
    return { target: 'product', product_type_id: productId, mode, version_id: versionId };
  }, [product, mode, trimmedNr, duplicate, predecessorId, versionId, productId]);

  const productName = product?.label ?? null;
  useEffect(() => {
    onChange({ productName, request });
  }, [productName, request, onChange]);

  if (loading) {
    return (
      <Banner kind="info">
        <Spinner /> <Trans>Produkt und Versionen werden geladen …</Trans>
      </Banner>
    );
  }
  if (loadError !== null || product === null) {
    const reason = loadError ?? t`unbekannt`;
    return <Banner kind="error"><Trans>Produkt konnte nicht geladen werden: {reason}</Trans></Banner>;
  }

  return (
    <section className="imp-target" aria-labelledby={`${idPrefix}-title`}>
      <h2 id={`${idPrefix}-title`}><Trans>Ziel der SBOM</Trans></h2>
      <div className="seg imp-target-mode" role="radiogroup" aria-label={t`Upload-Ziel`}>
        <button
          type="button"
          role="radio"
          aria-checked={mode === 'new'}
          className={mode === 'new' ? 'on' : undefined}
          disabled={disabled}
          onClick={() => setMode('new')}
        >
          <Trans>Neue Version anlegen</Trans>
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={mode === 'update'}
          className={mode === 'update' ? 'on' : undefined}
          disabled={disabled || versions.length === 0}
          title={versions.length === 0 ? t`Das Produkt hat noch keine offizielle Version.` : undefined}
          onClick={() => setMode('update')}
        >
          <Trans>Bestehende Version aktualisieren</Trans>
        </button>
      </div>

      {mode === 'new' ? (
        <div className="imp-target-fields">
          <label htmlFor={`${idPrefix}-nr`}>
            <Trans>Versionsnummer</Trans>
            <input
              id={`${idPrefix}-nr`}
              type="text"
              value={versionNr}
              disabled={disabled}
              required
              aria-invalid={duplicate || undefined}
              aria-describedby={duplicate ? `${idPrefix}-nr-error` : undefined}
              onChange={(event) => setVersionNr(event.target.value)}
              placeholder={t`z. B. 2.0`}
            />
          </label>
          {duplicate ? (
            <p id={`${idPrefix}-nr-error`} className="imp-target-error">
              <Trans>Version {trimmedNr} existiert bereits — wählen Sie „Bestehende Version aktualisieren“.</Trans>
            </p>
          ) : null}
          <label htmlFor={`${idPrefix}-pred`}>
            <Trans>Vorgängerversion (optional)</Trans>
            <select
              id={`${idPrefix}-pred`}
              value={predecessorId ?? ''}
              disabled={disabled}
              onChange={(event) => setPredecessorId(event.target.value === '' ? null : Number(event.target.value))}
            >
              <option value="">{t`Keine`}</option>
              {versions.map((row) => (
                <option key={row.id} value={row.id}>{versionLabel(row)}</option>
              ))}
            </select>
          </label>
        </div>
      ) : (
        <div className="imp-target-fields">
          <label htmlFor={`${idPrefix}-version`}>
            <Trans>Version</Trans>
            <select
              id={`${idPrefix}-version`}
              value={versionId ?? ''}
              disabled={disabled}
              required
              onChange={(event) => setVersionId(event.target.value === '' ? null : Number(event.target.value))}
            >
              <option value="" disabled>{t`Version wählen …`}</option>
              {versions.map((row) => (
                <option key={row.id} value={row.id}>{versionLabel(row)}</option>
              ))}
            </select>
          </label>
        </div>
      )}
    </section>
  );
}
